<script>
  import { getBinderColor, darkenColor } from '../../lib/logic/utils.js';
  import { BINDER_DARKEN_FACTOR } from '../../lib/logic/config.js';
  import { navigate } from '../../lib/stores/router.svelte.js';
  import ContextMenu from '../shared/ContextMenu.svelte';

  /** @type {{ binder: { url: string, name: string, owner?: string, hasTrade?: boolean, tradeId?: string, isStarred?: boolean, binderUrl?: string, wantCount?: number, cardCount?: number }, index?: number, showMenu?: boolean, badge?: import('svelte').Snippet, onStar?: (binder: any) => void, onRemove?: (binder: any) => void, onCancelTrade?: (binder: any) => void }} */
  let { binder, index = 0, showMenu = true, badge, onStar, onRemove, onCancelTrade } = $props();

  const colorUrl = $derived(binder.binderUrl || binder.url);
  const bgColor = $derived(getBinderColor(colorUrl));
  const borderColor = $derived(darkenColor(bgColor, BINDER_DARKEN_FACTOR));
  const animDelay = $derived(`${0.05 * (index + 1)}s`);

  // Context menu state
  let menuOpen = $state(false);
  let menuAnchor = $state(null);

  const menuItems = $derived.by(() => {
    const items = [];
    if (binder.hasTrade) {
      items.push({
        label: 'Cancel Trade',
        variant: 'warning',
        onClick: () => onCancelTrade?.(binder),
      });
      items.push({
        label: 'Remove Binder',
        variant: 'danger',
        onClick: () => onRemove?.(binder),
      });
    } else {
      items.push({
        label: binder.isStarred ? 'Unstar Binder' : 'Star this Binder',
        variant: 'star',
        onClick: () => onStar?.(binder),
      });
      items.push({
        label: 'Remove from Recents',
        variant: 'danger',
        onClick: () => onRemove?.(binder),
      });
    }
    return items;
  });

  function handleMenuClick(e) {
    e.preventDefault();
    e.stopPropagation();
    menuAnchor = e.currentTarget;
    menuOpen = true;
  }

  // Long-press for mobile
  let longPressTimer = null;
  let touchStartPos = { x: 0, y: 0 };

  function handleTouchStart(e) {
    if (!showMenu) return;
    touchStartPos = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    longPressTimer = setTimeout(() => {
      e.preventDefault();
      menuAnchor = e.currentTarget;
      menuOpen = true;
    }, 500);
  }

  function handleTouchMove(e) {
    if (!longPressTimer) return;
    const dx = e.touches[0].clientX - touchStartPos.x;
    const dy = e.touches[0].clientY - touchStartPos.y;
    if (Math.abs(dx) > 15 || Math.abs(dy) > 15) {
      clearTimeout(longPressTimer);
      longPressTimer = null;
    }
  }

  function handleTouchEnd() {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      longPressTimer = null;
    }
  }

  function handleContextMenu(e) {
    if (!showMenu) return;
    e.preventDefault();
  }

  function handleClick(e) {
    e.preventDefault();
    if (binder.hasTrade && binder.tradeId) {
      navigate('binder', { tradeId: binder.tradeId, tradeMode: true });
    } else {
      navigate('binder', { binderUrl: binder.url, shortPath: binder.url });
    }
  }
</script>

<li style="animation-delay: {animDelay}">
  <a
    href={binder.url}
    title={binder.hasTrade
      ? `Open trade with: ${binder.owner || binder.name}`
      : `Load binder from: ${binder.name}`}
    style="background-color: {bgColor}; --binder-border-color: {borderColor}; color: #1f2937;"
    onclick={handleClick}
    ontouchstart={handleTouchStart}
    ontouchmove={handleTouchMove}
    ontouchend={handleTouchEnd}
    oncontextmenu={handleContextMenu}
  >
    {#if binder.hasTrade}
      <span class="trade-active-label">trade active</span>
    {/if}

    {#if binder.isStarred}
      <span class="binder-star-sticker" title="Starred binder"></span>
    {/if}

    {@render badge?.()}

    {#if binder.wantCount && binder.wantCount > 0}
      <span
        class="want-count-badge"
        title="{binder.wantCount} card{binder.wantCount > 1 ? 's' : ''} from your want list"
      >
        {binder.wantCount} want{binder.wantCount > 1 ? 's' : ''}
      </span>
    {/if}

    <span class="binder-name">{binder.name}</span>

    {#if binder.owner}
      <span class="binder-owner">by {binder.owner}</span>
    {:else if binder.cardCount != null}
      <span class="binder-owner">{binder.cardCount.toLocaleString()} cards</span>
    {/if}

    {#if showMenu}
      <!-- 3-dot menu button (desktop only, mobile uses long-press) -->
      <!-- svelte-ignore a11y_click_events_have_key_events -->
      <span
        class="binder-menu-btn"
        title="More options"
        role="button"
        tabindex="-1"
        onclick={handleMenuClick}>&#8942;</span
      >
    {/if}
  </a>
</li>

{#if showMenu}
  <ContextMenu
    isOpen={menuOpen}
    items={menuItems}
    title={binder.name}
    anchorElement={menuAnchor}
    onClose={() => (menuOpen = false)}
  />
{/if}

<style>
  li {
    animation: fadeInUp 0.5s ease-out forwards;
    opacity: 0;
  }

  a {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    width: 130px;
    aspect-ratio: 5 / 7;
    padding: 0.75rem;
    border-top-left-radius: 2px;
    border-bottom-left-radius: 2px;
    border-top-right-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
    text-decoration: none;
    transition:
      filter var(--dur-slow) var(--ease-standard),
      box-shadow var(--dur-slow) var(--ease-standard),
      transform var(--dur-slow) var(--ease-standard);
    box-shadow:
      0 2px 4px 0 rgba(0, 0, 0, 0.08),
      0 1px 2px 0 rgba(0, 0, 0, 0.06);
    position: relative;
    border: 1px solid var(--binder-border-color, #d1d5db);
    border-left-width: 9px;
  }

  /* Dashed inner border (stitching effect) */
  a::after {
    content: '';
    position: absolute;
    top: 2px;
    bottom: 2px;
    left: 2px;
    right: 2px;
    border-radius: 2px;
    border: 1px dashed var(--binder-border-color, #9ca3af);
    opacity: 0.7;
    pointer-events: none;
  }

  a:hover {
    filter: brightness(1.1);
    box-shadow:
      0 8px 16px 0 rgba(0, 0, 0, 0.12),
      0 4px 8px 0 rgba(0, 0, 0, 0.08);
    transform: translateY(-4px) scale(1.02);
  }

  .binder-name {
    font-weight: 600;
    font-size: 0.95rem;
    line-height: 1.3;
    margin-bottom: 0.25rem;
  }

  .binder-owner {
    font-size: 0.75rem;
    font-weight: 500;
    opacity: 0.75;
    color: var(--binder-text-color, #374151);
  }

  .trade-active-label {
    position: absolute;
    bottom: 6px;
    left: 50%;
    transform: translateX(-50%);
    background-color: rgba(0, 0, 0, 0.65);
    color: white;
    font-size: 0.65rem;
    font-weight: 500;
    padding: 2px 8px;
    border-radius: 10px;
    white-space: nowrap;
    z-index: 10;
    text-transform: lowercase;
    letter-spacing: 0.02em;
  }

  .binder-star-sticker {
    position: absolute;
    top: 30px;
    right: 12px;
    transform: translateY(-50%) rotate(12deg);
    width: 44px;
    height: 44px;
    z-index: 5;
    pointer-events: none;
    filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.2));
  }

  .binder-star-sticker::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #fbbf24;
    -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z'/%3E%3C/svg%3E");
    mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z'/%3E%3C/svg%3E");
    -webkit-mask-size: 70%;
    mask-size: 70%;
    -webkit-mask-position: center;
    mask-position: center;
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
  }

  .binder-star-sticker::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: white;
    -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath stroke='black' stroke-width='3' stroke-linejoin='round' stroke-linecap='round' fill='black' d='M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z'/%3E%3C/svg%3E");
    mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath stroke='black' stroke-width='3' stroke-linejoin='round' stroke-linecap='round' fill='black' d='M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z'/%3E%3C/svg%3E");
    -webkit-mask-size: 70%;
    mask-size: 70%;
    -webkit-mask-position: center;
    mask-position: center;
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    z-index: -1;
  }

  .want-count-badge {
    position: absolute;
    bottom: 8px;
    left: 50%;
    transform: translateX(-50%);
    background: linear-gradient(135deg, #7c3aed, #9333ea);
    color: white;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 10px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    z-index: 1;
    pointer-events: none;
    white-space: nowrap;
  }

  /* 3-Dot Menu Button */
  .binder-menu-btn {
    position: absolute;
    top: -8px;
    right: -8px;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 50%;
    cursor: pointer;
    font-size: 16px;
    line-height: 1;
    color: #6b7280;
    opacity: 0;
    pointer-events: none;
    transition:
      opacity 0.15s,
      transform 0.15s,
      background 0.15s;
    transform: scale(0.8);
    z-index: 10;
  }

  a:hover .binder-menu-btn {
    opacity: 1;
    pointer-events: auto;
    transform: scale(1);
  }

  .binder-menu-btn:hover {
    background-color: #f3f4f6;
    color: #374151;
    border-color: #9ca3af;
  }

  /* Hide menu button on mobile - use long-press menu instead */
  @media (max-width: 768px) {
    .binder-menu-btn {
      display: none;
    }
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 640px) {
    a {
      width: 110px;
    }

    .binder-name {
      font-size: 0.875rem;
    }
  }

  @media (max-width: 768px) {
    a:hover {
      transform: none;
    }
  }
</style>
